import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { confirmRegistration } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import { navigateToBranded } from "@/lib/navigateToBranded";
import { getUtms } from "@/lib/getUtms";
import StepHeader from "@/components/StepHeader";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const ConfirmPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [consent, setConsent] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const registerData = sessionStorage.getItem("registerData");
  const tokenParam = searchParams.get("token");

  // Page guard
  useEffect(() => {
    if (!registerData && !tokenParam) navigate("/register");
  }, [registerData, tokenParam, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const parsed = registerData ? JSON.parse(registerData) : null;
    const token = parsed?.token?.uuid || tokenParam;
    const UTMs = getUtms();

    try {
      const result = await confirmRegistration(
        {
          consumer: { marketingConsent: consent },
          campaignId: sessionStorage.getItem("ls") || null,
          utm: UTMs?.utm_campaign || null,
        },
        token
      );
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "sign_up",
          user_account_source: sessionStorage.getItem("ls") || UTMs?.utm_campaign || "direct",
        });
      }
      sessionStorage.removeItem("registerData");
      navigateToBranded(result.data.token.uuid);
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(apiError.data.details, navigate);
      } else {
        setError(extractApiError(apiError));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!registerData && !tokenParam) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="absolute top-3 left-3 z-10 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
        onClick={() => { sessionStorage.removeItem("migration"); navigate("/"); }}
      >
        Anmelden
      </Button>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
      >
        <StepHeader stepTitle="Marketing-Einwilligung" step={5} migration={sessionStorage.getItem("migration") === "true"} />

        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          {/* Consent box */}
          <div className="flex flex-col gap-3 w-full">
            <div className="border border-black/20 rounded-xl p-4 flex flex-col gap-4 items-end">
              <div className="flex gap-4 items-start w-full">
                <button
                  type="button"
                  onClick={() => setConsent(!consent)}
                  className={`relative shrink-0 w-[28px] h-[16px] rounded-full transition-colors ${consent ? "bg-primary" : "bg-[#7f7f7f]"}`}
                >
                  <span
                    className={`absolute top-[2px] w-[12px] h-[12px] rounded-full bg-white transition-transform ${consent ? "left-[14px]" : "left-[2px]"}`}
                  />
                </button>
                <p className="text-[10px] leading-[14px] text-black flex-1">
                  Ja, ich möchte Warenproben, Informationen und Angebote
                  (individualisierte Angebote und Kundenumfragen) von der British
                  American Tobacco (Germany) GmbH (BAT), Alsterufer 4, 20354
                  Hamburg an meine oben genannte Anschrift erhalten. Zudem möchte
                  ich Newsletter, individualisierte Angebote und Kundenumfragen
                  per E-Mail, SMS und Telefon von BAT erhalten. Ich bin damit
                  einverstanden, dass BAT zu diesen Zwecken meine persönlichen
                  Produkt-Präferenzen auf Basis der von BAT über mich
                  verarbeiteten Daten in einem individuellen Profil
                  zusammenführt.{" "}
                  <a href="/legal/datenschutz" className="underline font-bold">
                    Datenschutz - Wie wir deine Daten schützen | Lucky Strike
                  </a>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1.5 text-[10px] font-bold text-black uppercase"
              >
                {expanded ? "Weniger sehen" : "Mehr sehen"}
                {expanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {expanded && (
              <>
                <div className="border border-[#ababab] rounded-xl p-4">
                  <p className="text-[12px] leading-[16px] text-black font-medium">
                    Die obige Einwilligung betrifft folgende Marken: Pall Mall,
                    Lucky Strike, Fair Play, glo™, Vuse.
                  </p>
                </div>
                <div className="border border-[#ababab] rounded-xl p-4">
                  <p className="text-[12px] leading-[16px] text-black font-medium">
                    Hinweis: Du kannst Deine Einwilligung jederzeit mit Wirkung
                    für die Zukunft widerrufen. Nähere Informationen findest du
                    in unseren{" "}
                    <a href="/legal/datenschutz" className="underline">
                      Datenschutzbestimmungen.
                    </a>
                    <br />
                    Es gelten unsere{" "}
                    <a
                      href="/legal/nutzungsbedingungen"
                      className="underline"
                    >
                      Teilnahmebedingungen
                    </a>
                    .
                  </p>
                </div>
              </>
            )}
          </div>

          <ErrorModal
            open={!!error}
            onClose={() => setError(null)}
            message={error?.message}
            errorCode={error?.errorCode}
          />

          <Button
            type="submit"
            className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
            size="sm"
            disabled={isLoading}
          >
            {isLoading ? "Bitte warte..." : "Bestätigen"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default ConfirmPage;
